import { useState } from "react";
import { AdminThumb } from "@/components/admin/AdminThumb";
import axios from "axios";
import { TextT, ArrowsClockwise, FloppyDisk, Sparkle } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { API } from "@/lib/api";
import { BulkGenreBar } from "@/components/admin/BulkGenreBar";
import { useGenres, GenreOptions } from "@/lib/genres";

const field = "w-full bg-[#111] border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber";
const btn = "flex items-center gap-1.5 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 disabled:opacity-40";

const GenrePicker = ({ item, headers, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const { groups, label } = useGenres();
  const pick = async (genre) => {
    if (!genre) return;
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/media/${item.video_id}/genre`, { genre }, { headers, withCredentials: true });
      onSaved({ ...data, needs_review: false, genre_review_reason: null });
      toast.success(`Genre set to ${label(genre)} — old tags cleared`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Genre update failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <select
      data-testid={`primary-genre-select-${item.video_id}`}
      value={item.primary_genre || ""}
      disabled={busy}
      onChange={(e) => pick(e.target.value)}
      className="bg-[#111] border border-emerald-500/40 px-2 py-2 font-mono text-[10px] uppercase tracking-[0.12em] text-emerald-200 focus:outline-none focus:border-emerald-400 disabled:opacity-40"
    >
      <option value="">Primary genre…</option>
      <GenreOptions groups={groups} />
    </select>
  );
};

const AutoCategorizeButton = ({ headers, total, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!window.confirm(`Auto-categorize all ${total ?? ""} films in Needs Review from their tags? Films with clashing or no genre stay in the queue.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/media/auto-categorize`, {}, { headers, withCredentials: true });
      setRes(data);
      toast.success(`${data.categorized} films pushed live · ${data.remaining} left for review`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-categorize failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex basis-full flex-wrap items-center gap-3">
      <button type="button" data-testid="auto-categorize-btn" onClick={run} disabled={busy}
        className="flex items-center gap-2 bg-amber px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-amber/80 disabled:opacity-40">
        <Sparkle size={15} weight="fill" /> {busy ? "Categorizing…" : "Auto-Categorize via Tags"}
      </button>
      {res && (
        <span data-testid="auto-categorize-result" className="font-mono text-[10px] text-white/55">
          {res.categorized} live · {res.held_conflict} clashing · {res.no_match} no genre found
        </span>
      )}
    </div>
  );
};

const Row = ({ item, headers, onSaved, checked, onCheck }) => {
  const [val, setVal] = useState(item.title_override || "");
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    setBusy(kind);
    try {
      const url = `${API}/admin/media/${item.video_id}/${kind === "save" ? "title" : "sync-tmdb-title"}`;
      const { data } = kind === "save"
        ? await axios.put(url, { title_override: val }, { headers, withCredentials: true })
        : await axios.post(url, {}, { headers, withCredentials: true });
      setVal(data.title_override || "");
      onSaved(data);
      toast.success(kind === "save" ? "Title override saved" : `Synced: ${data.title_override || data.tmdb_title || data.title}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy("");
    }
  };
  return (
    <li className={`grid gap-2 border-b border-white/5 py-3 md:items-center ${onCheck ? "md:grid-cols-[auto_120px_1fr_1fr_auto]" : "md:grid-cols-[120px_1fr_1fr_auto]"} ${checked ? "bg-amber/[0.06]" : ""}`} data-testid={`title-row-${item.video_id}`}>
      {onCheck && (
        <input type="checkbox" data-testid={`bulk-check-${item.video_id}`} checked={checked} onChange={() => onCheck(item.video_id)}
          aria-label={`Select ${item.title}`} className="h-4 w-4 cursor-pointer accent-amber" />
      )}
      <AdminThumb url={item.monetized_embed_url} thumb={item.default_thumb} testid={`title-row-thumb-${item.video_id}`} />
      <div className="min-w-0">
        <p className="truncate text-sm text-white/80" title={item.title}>{item.title}</p>
        <p className="font-mono text-[10px] text-white/35">{item.video_id}{item.tmdb_title ? ` · TMDb: ${item.tmdb_title}` : ""}{item.title_clean ? ` · Cleaned: ${item.title_clean}` : ""}</p>
        <p data-testid={`title-row-tags-${item.video_id}`} className="font-mono text-[10px] text-emerald-300/60 truncate">
          {item.genre_review_reason && <span className="text-crimson">{item.genre_review_reason} · </span>}
          Tags: {(item.keywords || []).join(", ") || "none"}
        </p>
      </div>
      <input data-testid={`title-override-input-${item.video_id}`} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Title Override (blank = use TMDb / scraped title)" className={field} />
      <div className="flex flex-wrap gap-2">
        <GenrePicker item={item} headers={headers} onSaved={onSaved} />
        <button data-testid={`title-override-save-${item.video_id}`} onClick={() => run("save")} disabled={!!busy} className={`${btn} border-amber/50 text-amber hover:bg-amber hover:text-black`}>
          <FloppyDisk size={13} /> Save
        </button>
        <button data-testid={`title-sync-tmdb-${item.video_id}`} onClick={() => run("sync")} disabled={!!busy} className={`${btn} border-sky-300/40 text-sky-200 hover:bg-sky-300 hover:text-black`}>
          <ArrowsClockwise size={13} className={busy === "sync" ? "animate-spin" : ""} /> Sync TMDb Title
        </button>
      </div>
    </li>
  );
};

export const TitleOverridePanel = ({ headers }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [reviewTotal, setReviewTotal] = useState(null);
  const [reviewPage, setReviewPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const load = async (params) => {
    try {
      const { data } = await axios.get(`${API}/admin/media`, { params, headers, withCredentials: true });
      setItems(data.items);
      setReviewTotal(data.review_total);
      setReviewPage(params.review ? data.page : 0);
      setSelected([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    }
  };
  const search = (e) => {
    e?.preventDefault();
    load({ q });
  };
  const patch = (d) => setItems((list) => list.map((i) => (i.video_id === d.video_id ? { ...i, ...d } : i)));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = items.length > 0 && items.every((i) => selected.includes(i.video_id));
  const pages = Math.max(1, Math.ceil((reviewTotal || 0) / 50));
  const goPage = (p) => load({ review: true, page: p });
  return (
    <section data-testid="title-override-panel" className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
      <div className="mb-4 flex items-center gap-3">
        <TextT size={22} weight="duotone" className="text-amber" />
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Official Titles · Title Override</h2>
      </div>
      <form onSubmit={search} className="flex flex-wrap gap-2">
        <input data-testid="title-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a title or paste a video ID" className={`${field} min-w-0 basis-full sm:basis-auto sm:flex-1`} />
        <button data-testid="title-search-btn" type="submit" className={`${btn} border-white/20 text-white/80 hover:border-amber hover:text-amber`}>Search</button>
        <button data-testid="title-review-queue-btn" type="button" onClick={() => load({ review: true })} className={`${btn} border-crimson/50 text-crimson hover:bg-crimson hover:text-white whitespace-nowrap`}>
          Needs review{reviewTotal != null ? ` · ${reviewTotal}` : ""}
        </button>
        <button data-testid="genre-cleanup-btn" type="button" onClick={async () => {
          try {
            await axios.post(`${API}/admin/genre-cleanup`, {}, { headers, withCredentials: true });
            toast.success("Genre cleanup started — takes a few minutes");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start cleanup");
          }
        }} className={`${btn} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500 hover:text-black whitespace-nowrap`}>
          Clean genre tags
        </button>
      </form>
      <p className="mt-2 font-mono text-[10px] text-white/35">New YouTube imports are cleaned and matched to TMDb automatically. Titles with no confident match land in "Needs review". Picking a Primary Genre wipes the old YouTube tags (hub tags like Kids/Shorts stay).</p>
      {reviewPage > 0 && (
        <div data-testid="review-toolbar" className="mt-4 flex flex-wrap items-center gap-4 border-b border-white/10 pb-3">
          <AutoCategorizeButton headers={headers} total={reviewTotal} onDone={() => goPage(1)} />
          <label className="flex cursor-pointer items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/70">
            <input type="checkbox" data-testid="bulk-select-all" checked={allOn}
              onChange={() => setSelected(allOn ? [] : items.map((i) => i.video_id))} className="h-4 w-4 accent-amber" />
            Select all on page
          </label>
          <div className="ml-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/50">
            <button type="button" data-testid="review-prev-page" disabled={reviewPage <= 1} onClick={() => goPage(reviewPage - 1)} className={`${btn} border-white/15 hover:border-amber hover:text-amber`}>Prev</button>
            <span data-testid="review-page-indicator">Page {reviewPage} / {pages}</span>
            <button type="button" data-testid="review-next-page" disabled={reviewPage >= pages} onClick={() => goPage(reviewPage + 1)} className={`${btn} border-white/15 hover:border-amber hover:text-amber`}>Next</button>
          </div>
        </div>
      )}
      <ul className="mt-2">
        {items.map((it) => (
          <Row key={it.video_id} item={it} headers={headers} onSaved={patch}
            checked={selected.includes(it.video_id)} onCheck={reviewPage > 0 ? toggle : null} />
        ))}
      </ul>
      <BulkGenreBar selected={selected} headers={headers}
        onClear={() => setSelected([])}
        onDone={(d) => goPage(Math.min(reviewPage, Math.max(1, Math.ceil(d.review_total / 50))))} />
    </section>
  );
};
